import { useState } from 'react';

export interface AuthenticateSubmit {
  mode: 'totp' | 'recovery';
  code: string;
  ttlMin: number | null;
}

export interface AuthenticateProps {
  busy?: boolean;
  errorMessage?: string | null;
  onSubmit: (payload: AuthenticateSubmit) => void;
  onCancel: () => void;
}

const placeholders = {
  totp: '123456',
  recovery: 'a3b9k2x5p4-q8h1m7n0w3',
} as const;

export default function Authenticate({
  busy,
  errorMessage,
  onSubmit,
  onCancel,
}: AuthenticateProps) {
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');
  const [persistSession, setPersistSession] = useState(false);
  const [sessionMin, setSessionMin] = useState(30);

  const valid =
    mode === 'totp'
      ? /^\d{6}$/.test(code)
      : /^[a-z0-9-]{15,30}$/i.test(code.trim());

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    onSubmit({
      mode,
      code: code.trim(),
      ttlMin: persistSession && mode === 'totp' ? sessionMin : null,
    });
  }

  function switchMode(next: 'totp' | 'recovery') {
    setMode(next);
    setCode('');
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 rounded-lg border border-ink-800 bg-ink-900/60 p-1">
        <button
          type="button"
          onClick={() => switchMode('totp')}
          className={[
            'flex-1 rounded-md px-3 py-1.5 text-xs transition-colors',
            mode === 'totp'
              ? 'bg-ink-800 text-ink-50'
              : 'text-ink-300 hover:text-ink-100',
          ].join(' ')}
        >
          Authenticator code
        </button>
        <button
          type="button"
          onClick={() => switchMode('recovery')}
          className={[
            'flex-1 rounded-md px-3 py-1.5 text-xs transition-colors',
            mode === 'recovery'
              ? 'bg-ink-800 text-ink-50'
              : 'text-ink-300 hover:text-ink-100',
          ].join(' ')}
        >
          Recovery code
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode={mode === 'totp' ? 'numeric' : 'text'}
          autoComplete="one-time-code"
          maxLength={mode === 'totp' ? 6 : 30}
          placeholder={placeholders[mode]}
          className={[
            'w-full rounded-lg border border-ink-800 bg-ink-900 px-4 py-3 text-center font-mono tracking-widest text-ink-50 focus:border-avo-500 focus:outline-none',
            mode === 'totp' ? 'text-xl' : 'text-base',
          ].join(' ')}
        />

        {mode === 'totp' && (
          <label className="flex items-center gap-2 text-sm text-ink-200">
            <input
              type="checkbox"
              checked={persistSession}
              onChange={(e) => setPersistSession(e.target.checked)}
              className="accent-avo-500"
            />
            <span>Remember TOTP for the next</span>
            <select
              value={sessionMin}
              onChange={(e) => setSessionMin(Number(e.target.value))}
              className="bg-ink-900 border border-ink-800 rounded px-2 py-0.5 text-sm"
            >
              <option value={5}>5 min</option>
              <option value={15}>15 min</option>
              <option value={30}>30 min</option>
              <option value={60}>1 hour</option>
              <option value={240}>4 hours</option>
            </select>
          </label>
        )}

        {errorMessage && (
          <p className="text-sm text-red-400">{errorMessage}</p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 rounded-lg border border-ink-800 bg-ink-900/60 px-4 py-2.5 text-sm text-ink-100 hover:bg-ink-800/60 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || busy}
            className="flex-1 rounded-lg bg-avo-500 px-4 py-2.5 text-sm font-medium text-ink-950 hover:bg-avo-400 transition-colors disabled:opacity-50"
          >
            {busy ? 'Verifying…' : 'Authorize'}
          </button>
        </div>
      </form>
    </div>
  );
}
